import { useEffect, useMemo } from 'react';
import { useFilterRegistry } from '../WbResponsiveTableToolbar';
import { v4 as uuidv4 } from 'uuid';

/**
 * Helper function to determine if a filter value is considered "active"
 * (i.e., should be counted in the active filters badge)
 */
const isFilterValueActive = (value: any): boolean => {
  // Null, undefined, empty string, 0, and false are considered inactive
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    value === 0 ||
    value === false
  ) {
    return false;
  }

  // For arrays, check if they have items
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  // For objects, check if they have keys (optional, can be customized)
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  // All other truthy values are considered active
  return true;
};

/**
 * Hook to register a filter's active state with the parent WbResponsiveTableToolbar.
 * This allows the toolbar to count active filters even for wrapper components
 * that don't directly expose their value prop.
 *
 * @param value - The current value of the filter
 * @param filterName - Optional name for the filter (used for debugging)
 * @returns void
 *
 * @example
 * ```tsx
 * const MyCustomFilter = () => {
 *   const [selectedValue, setSelectedValue] = useState('');
 *
 *   // Register this filter with the toolbar
 *   useFilterRegistration(selectedValue, 'MyCustomFilter');
 *
 *   return <SelectFilter value={selectedValue} onChange={setSelectedValue} />;
 * };
 * ```
 */
export const useFilterRegistration = (
  value: any,
  filterName?: string,
): void => {
  const filterRegistry = useFilterRegistry();

  const filterId = useMemo(
    () => `${filterName || 'filter'}-${uuidv4()}`,
    [filterName],
  );

  useEffect(() => {
    if (!filterRegistry) {
      return undefined;
    }

    const hasActiveValue = isFilterValueActive(value);
    filterRegistry.registerFilter(filterId, hasActiveValue);

    return () => {
      filterRegistry.unregisterFilter(filterId);
    };
  }, [filterRegistry, filterId, value]);
};
